interface LoadedScripts {
  [url: string]: {
    element: HTMLScriptElement
    loaded: boolean
    loading: boolean
  }
}

interface ScriptLoadOptions {
  async?: boolean
  defer?: boolean
  id?: string
}

export default defineNuxtPlugin(() => {
  // Only run on client side
  if (process.server) return

  // Check if AI bot is enabled via environment variable
  const config = useRuntimeConfig()
  const isAiBotEnabled = config.public.aiBotEnabled === 'true'

  if (!isAiBotEnabled) {
    return
  }

  const { $i18n } = useNuxtApp()
  const loadedScripts: LoadedScripts = {}

  // External script configuration
  const EXTERNAL_SCRIPT_URL = 'https://api.outboundhq.com/widgets/assistants/9ufeI3EN2wpT.js'
  const TARGET_LOCALE = 'da'

  /**
   * Loads an external script with performance optimizations and error handling
   */
  const loadScript = async (
    url: string,
    options: ScriptLoadOptions = {}
  ): Promise<HTMLScriptElement> => {
    const {
      async = true,
      defer = false,
      id = `script-${btoa(url).slice(0, 8)}`
    } = options

    // Check if script is already loaded or loading
    if (loadedScripts[url]) {
      if (loadedScripts[url].loaded) {
        return loadedScripts[url].element
      }
      if (loadedScripts[url].loading) {
        // Wait for ongoing load to complete
        return new Promise((resolve, reject) => {
          const checkLoad = () => {
            if (loadedScripts[url].loaded) {
              resolve(loadedScripts[url].element)
            } else {
              setTimeout(checkLoad, 50)
            }
          }
          checkLoad()
        })
      }
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector(`script[src="${url}"], script[id="${id}"]`)
    if (existingScript) {
      const scriptElement = existingScript as HTMLScriptElement
      loadedScripts[url] = {
        element: scriptElement,
        loaded: true,
        loading: false
      }
      return scriptElement
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')

      // Track loading state
      loadedScripts[url] = {
        element: script,
        loaded: false,
        loading: true
      }

      // Configure script attributes
      Object.assign(script, {
        src: url,
        async,
        defer,
        id,
        type: 'text/javascript'
      })

      // Handle successful load
      const onLoad = () => {
        loadedScripts[url].loaded = true
        loadedScripts[url].loading = false
        script.removeEventListener('load', onLoad)
        script.removeEventListener('error', onError)
        resolve(script)
      }

      // Handle load error
      const onError = (error: Event) => {
        delete loadedScripts[url]
        script.removeEventListener('load', onLoad)
        script.removeEventListener('error', onError)
        script.remove()
        reject(new Error(`Failed to load script: ${url}`))
      }

      script.addEventListener('load', onLoad)
      script.addEventListener('error', onError)

      // Append to head for better performance
      document.head.appendChild(script)
    })
  }

  /**
   * Loads the AI bot script if locale matches target
   */
  const loadAiBotScript = async (): Promise<void> => {
    try {
      const currentLocale = $i18n.locale.value

      if (currentLocale === TARGET_LOCALE) {
        await loadScript(EXTERNAL_SCRIPT_URL, {
          id: 'ai-bot-script',
          async: true,
          defer: true
        })
        console.log('[AI Bot] Script loaded successfully for locale:', currentLocale)
      }
    } catch (error) {
      console.error('[AI Bot] Failed to load script:', error)
    }
  }

  /**
   * Cleanup function to remove scripts when locale changes away from target
   */
  const cleanupAiBotScript = (): void => {
    const script = document.querySelector('#ai-bot-script')
    if (script) {
      script.remove()
      delete loadedScripts[EXTERNAL_SCRIPT_URL]
      console.log('[AI Bot] Script removed for locale change')
    }
  }

  // Load script immediately if current locale matches
  loadAiBotScript()

  // Watch for locale changes using Nuxt 3 i18n
  watch(
    () => $i18n.locale.value,
    (newLocale: string, oldLocale: string) => {
      if (newLocale === TARGET_LOCALE) {
        loadAiBotScript()
      } else if (oldLocale === TARGET_LOCALE) {
        cleanupAiBotScript()
      }
    },
    { immediate: false }
  )

  // Cleanup on page navigation
  const router = useRouter()
  router.beforeEach(() => {
    // Optional: Remove script on navigation if needed
    // cleanupAiBotScript()
  })

  // Provide utilities for other components if needed
  return {
    provide: {
      aiBot: {
        loadScript: loadAiBotScript,
        cleanup: cleanupAiBotScript,
        isLoaded: () => loadedScripts[EXTERNAL_SCRIPT_URL]?.loaded || false
      }
    }
  }
})
